import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ShieldCheck,
  ChartLineUp,
  UsersThree,
  BellRinging,
  FileText,
  GearSix,
  SignOut,
  UploadSimple,
  Tag,
  Sparkle,
  ClockCounterClockwise,
  ShieldWarning,
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink to={to} data-testid={testId} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
    <Icon size={18} weight="bold" />
    <span>{label}</span>
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-slate-950 border-r border-white/10 px-4 py-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 px-2 mb-8" data-testid="sidebar-brand">
          <div className="w-10 h-10 rounded-xl bg-orange-500 grid place-items-center shadow-lg shadow-orange-500/25">
            <ShieldCheck size={22} weight="bold" color="#fff7ed" />
          </div>
          <div>
            <div className="font-display font-black text-slate-50 leading-none">AuditReady</div>
            <div className="font-label text-orange-400 mt-1">SPORTS</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1">
          <div className="font-label text-slate-500 px-2 mb-2">MAIN</div>
          <NavItem to="/dashboard" icon={ChartLineUp} label="Dashboard" testId="nav-dashboard" />
          <NavItem to="/executive" icon={Sparkle} label="Executive" testId="nav-executive" />
          <NavItem to="/staff" icon={UsersThree} label="Staff" testId="nav-staff" />
          <NavItem to="/alerts" icon={BellRinging} label="Alerts" testId="nav-alerts" />
          <NavItem to="/verifications" icon={ShieldWarning} label="Verifications" testId="nav-verifications" />
          <NavItem to="/reports" icon={FileText} label="Reports" testId="nav-reports" />
          <div className="font-label text-slate-500 px-2 mb-2 mt-6">TOOLS</div>
          <NavItem to="/import" icon={UploadSimple} label="CSV Import" testId="nav-import" />
          <NavItem to="/import/history" icon={ClockCounterClockwise} label="Import History" testId="nav-import-history" />
          <NavItem to="/pricing" icon={Tag} label="Pricing" testId="nav-pricing" />
          <div className="font-label text-slate-500 px-2 mb-2 mt-6">ACCOUNT</div>
          <NavItem to="/settings" icon={GearSix} label="Settings" testId="nav-settings" />
        </nav>

        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9 border border-white/10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-slate-800 text-slate-200 text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-100 truncate" data-testid="user-name">{user?.name || "User"}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start mt-2 text-slate-400 hover:text-slate-50 hover:bg-white/5"
            onClick={handleLogout}
          >
            <SignOut size={16} weight="bold" className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-slate-950/95 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-orange-500 grid place-items-center">
            <ShieldCheck size={18} weight="bold" color="#fff7ed" />
          </div>
          <span className="font-display font-black">AuditReady <span className="text-orange-400">Sports</span></span>
        </div>
        <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="logout-button-mobile">
          <SignOut size={16} weight="bold" />
        </Button>
      </div>

      <main className="flex-1 min-w-0 md:pt-0 pt-16">
        {/* Mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-slate-950/95 backdrop-blur-xl border-t border-white/10 grid grid-cols-4 py-2">
          <NavLink to="/dashboard" className={({isActive}) => `flex flex-col items-center gap-1 text-xs py-1 ${isActive?"text-orange-400":"text-slate-400"}`}>
            <ChartLineUp size={20} weight="bold" /><span>Home</span>
          </NavLink>
          <NavLink to="/staff" className={({isActive}) => `flex flex-col items-center gap-1 text-xs py-1 ${isActive?"text-orange-400":"text-slate-400"}`}>
            <UsersThree size={20} weight="bold" /><span>Staff</span>
          </NavLink>
          <NavLink to="/alerts" className={({isActive}) => `flex flex-col items-center gap-1 text-xs py-1 ${isActive?"text-orange-400":"text-slate-400"}`}>
            <BellRinging size={20} weight="bold" /><span>Alerts</span>
          </NavLink>
          <NavLink to="/reports" className={({isActive}) => `flex flex-col items-center gap-1 text-xs py-1 ${isActive?"text-orange-400":"text-slate-400"}`}>
            <FileText size={20} weight="bold" /><span>Reports</span>
          </NavLink>
        </div>

        <div className="pb-24 md:pb-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
